import { select, Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  QueryReport,
  BodyDataField,
  Table,
  DBFieldMap,
  CommonDataBody,
  FormField,
  BODY_FIELD_TYPES,
  ConditionGenerator,
  Condition,
  ConditionalGroup,
} from '../state/_models/ignatius.models';
import { getApplicationData } from '../state/_selectors/ignatius.selector';

/**
 * Remove table prefix
 */
export const removeTablePrefix = (queryResponse: Array<any>) => {
  let tableData: Array<QueryReport> = [];
  for (let table of queryResponse) {
    let tableObject = <QueryReport>{};
    for (let item in table) {
      if (item.indexOf('_') > -1 && item.substr(0, 3) === 'tbl') {
        let key = item;
        while (key.includes('tbl')) {
          key = key.split('_').splice(1).join('_');
        }
        tableObject[key] = table[item];
      } else tableObject[item] = table[item];
    }
    tableData.push(tableObject);
    tableObject = <QueryReport>{};
  }
  return tableData;
};

/**
 * Converts form fields to postdata api request data
 */
export const convertFields = (
  applicationTableId: string,
  fields: FormField
) => {
  let body: CommonDataBody = <CommonDataBody>{
    fieldsList: [],
    ApplicationTableKey: applicationTableId,
    ApplicationKey: environment.applicationKey,
  };
  for (let field in fields)
    if (typeof fields[field] !== 'object')
      body.fieldsList.push(getFieldObject(field, fields[field]));
    else
      body.fieldsList.push(
        getFileFieldObject(field, fields[field].name, fields[field].value)
      );
  return body;
};

/**
 * Post data form field object
 * @param key
 * @param value
 */
const getFieldObject = (key: string, value: string): BodyDataField => {
  return {
    Name: key,
    Value: value,
  };
};

/**
 * Post data form file field object
 * @param key
 * @param value
 */
const getFileFieldObject = (
  key: string,
  value: string,
  base64File: string
): BodyDataField => {
  return {
    Name: key,
    Value: value,
    Base64EncodedFile: base64File,
  };
};

/**
 * Resolves DB Fields to get ID from it
 */
export const resolveDBFields = (
  tables: Array<Table>,
  ApplicationTableKey: string
): DBFieldMap | null => {
  for (let table of tables) {
    if (table.Key === ApplicationTableKey) {
      let DBFieldMapping: DBFieldMap = <DBFieldMap>{};
      table.Fields.forEach((field) => {
        DBFieldMapping[field.DbName] = field.Key;
      });
      return DBFieldMapping;
    }
  }
  return null;
};

/**
 * Create a map to DB Field's Id and DB Field name for better performance on
 * Conditional query report
 */
export const fieldIdToName = (tables: Array<Table>) => {
  let idToFieldMapping: any = {};
  tables.forEach((table) => {
    table.Fields.forEach((field) => {
      idToFieldMapping[field.Key] = field.DbName;
    });
  });
  return idToFieldMapping;
};

/**
 * Convert file to base 64
 * @param file selected file
 * @returns base64 string of file
 */
export const fileToBase64 = (file: Blob | any, split: boolean = true) => {
  let name = file['name']
    ? file['name'].replaceAll(/[&\/\\#,+()$~%'":!*?<>{}]/g, '')
    : file['name'];
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve({
        name,
        value: split ? reader.result!.toString().split(',')[1] : reader.result!.toString(),
      });
    };
  });
};

/**
 * Convert remote file to base64
 * @param url path of file
 * @returns object with name, value
 */
export const remoteFileToBase64 = async (url: string, split: boolean = true) => {
  const response = await fetch(url);
  return await fileToBase64(await response.blob(), split);
};

/**
 * Generate conditional query
 * This method is meant to use iff there exists only 1 condition in each conditional group
 */
export const conditionGenerator = (
  ...conditions: Array<ConditionGenerator>
): Array<ConditionalGroup> => {
  let conditionalGroups: Array<ConditionalGroup> = [];
  conditions.forEach((condition: ConditionGenerator) => {
    conditionalGroups.push({
      Type: condition.type || 'any',
      Conditions: [
        {
          ConditionField: {
            Id: condition.id,
          },
          OperationType: condition.opType || 'is equal',
          Value: condition.value,
        },
      ],
    });
  });
  return conditionalGroups;
};
